import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import {
  IAccessAlertAdmin,
  IAccessAlertChild,
  IAccessChangeStudent,
  IAccessChangeTrail,
  ILinkedAccountInfo,
} from 'src/app/core/models/access';
import { IGuardian, IStudent } from 'src/app/core/models/user';
import { AccessActions } from 'src/app/core/store/access/access.actions';
import { AppState } from 'src/app/core/store/app.state';
import { GuardianActions } from 'src/app/core/store/guardian/guardian.actions';
import {
  selectAdminGuardian,
  selectGuardianLinks,
  selectGuardians,
  selectLoggedInGuardian,
  selectLoggedInGuardianIsCoParent,
} from 'src/app/core/store/guardian/guardian.selectors';
import { StudentActions } from 'src/app/core/store/students/student.actions';
import { selectAllActiveStudents, selectStudentsForAccesschange } from 'src/app/core/store/students/student.selectors';
import { AccessAlertComponent } from 'src/app/features/home/components/access-alert/access-alert.component';
import { AccessService } from './access.service';

@Injectable({
  providedIn: 'root',
})
export class AccessChangeMonitorService {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly accessService = inject(AccessService);
  private readonly dialog = inject(MatDialog);

  private readonly guardians = this.store.selectSignal(selectGuardians);
  private readonly guardianLinks = this.store.selectSignal(selectGuardianLinks);
  private readonly loggedInGuardian = this.store.selectSignal(selectLoggedInGuardian);
  private readonly loggedInGuardianIsCoParent = this.store.selectSignal(selectLoggedInGuardianIsCoParent);
  private readonly fallbackAdminGuardian = this.store.selectSignal(selectAdminGuardian);
  private readonly accessChangeStudents = this.store.selectSignal(selectStudentsForAccesschange);
  private readonly learnerList = this.store.selectSignal(selectAllActiveStudents);

  private alertOpen = false;
  private checkingAccessChanges = false;
  private changeTrails: IAccessChangeTrail[] = [];
  private readonly justAcceptedTermsKey = 'myhub.justAcceptedTermsOfUse';

  async checkAndOpenAccessAlertIfNeeded(): Promise<void> {
    if (this.checkingAccessChanges || this.alertOpen) return;
    this.checkingAccessChanges = true;

    try {
      const loggedInGuardian = await this.ensureGuardianContext();
      if (!loggedInGuardian?.idPassport || !this.loggedInGuardianIsCoParent()) {
        return;
      }

      const justAcceptedTermsThisSession = this.hasJustAcceptedTermsFlag();

      if (justAcceptedTermsThisSession && this.learnerList().length === 0) {
        this.openAccessAlert({ firstLoginNoLearnersAlert: true });
        return;
      }

      // Fetch network changes FIRST, so the Trails are populated for the Admin resolution
      const changes = await firstValueFrom(this.accessService.getAccessChanges(loggedInGuardian.idPassport));
      const trails = changes?.changeTrails ?? [];
      this.changeTrails = trails;

      // Give NgRx stores a brief moment to hydrate the learnerList and guardianLinks on first load
      let hydrateWait = 0;
      while (this.learnerList().length === 0 && this.guardianLinks().length === 0 && hydrateWait < 15) {
        await new Promise((r) => setTimeout(r, 200));
        hydrateWait++;
      }

      let admin = this.resolveAdminGuardian();
      if (!admin) {
        console.warn('No admin guardian found dynamically in store. Fallback to Primary Admin.');
        admin = { firstname: 'The Primary', lastname: 'Admin', email: '' } as IAccessAlertAdmin;
      }

      this.store.dispatch(
        AccessActions.setAccessAlertAdmin({
          accessAlertAdmin: {
            firstname: admin.firstname,
            lastname: admin.lastname,
            email: admin.email,
          },
        }),
      );

      const hasUnconfirmed = trails.some((trail) => trail.changeConfirmed === 0 || trail.changeConfirmed === false);
      if (!hasUnconfirmed) {
        return;
      }

      const studentIds = [...new Set(trails.map((trail) => trail.studentId).filter(Boolean))] as string[];
      if (studentIds.length > 0) {
        this.store.dispatch(StudentActions.loadStudentsForAccesschange({ studentIds }));
        await this.waitForAccessChangeStudents(studentIds);
      }

      this.openAccessAlert();
    } catch (error) {
      console.warn('Failed to check access changes', error);
    } finally {
      this.checkingAccessChanges = false;
    }
  }

  private async ensureGuardianContext() {
    let loggedInGuardian = this.loggedInGuardian();
    if (!loggedInGuardian?.idPassport) {
      this.store.dispatch(GuardianActions.loadLoggedinGuardian());
      loggedInGuardian = await this.waitForLoggedInGuardian();
    }

    if (!this.guardians().length) {
      this.store.dispatch(GuardianActions.loadGuardians());
      await this.waitForGuardians();
    }

    return loggedInGuardian;
  }

  private hasJustAcceptedTermsFlag(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }

    return sessionStorage.getItem(this.justAcceptedTermsKey) === 'true';
  }

  private async waitForLoggedInGuardian() {
    return firstValueFrom(
      this.store.select(selectLoggedInGuardian).pipe(
        filter((guardian) => !!guardian?.id && !!guardian?.idPassport),
        take(1),
        timeout(10000),
      ),
    );
  }

  private async waitForGuardians(): Promise<IGuardian[]> {
    return firstValueFrom(
      this.store.select(selectGuardians).pipe(
        filter((guardians) => Array.isArray(guardians) && guardians.length > 0),
        take(1),
        timeout(10000),
      ),
    );
  }

  private waitForAccessChangeStudents(studentIds: string[]): Promise<void> {
    return firstValueFrom(
      this.store.select(selectStudentsForAccesschange).pipe(
        filter((students) => {
          const loadedIds = new Set(students.map((student) => student.id));
          return studentIds.every((id) => loadedIds.has(id));
        }),
        take(1),
        timeout(10000),
      ),
    )
      .then(() => void 0)
      .catch(() => {
        console.warn('Timed out waiting for access change students to load');
      });
  }

  private getAccessAlertAction(): 'granted' | 'removed' | 'mixed' | 'none' {
    const hasGranted = this.changeTrails.some((trail) => trail.changeType === 'GRANTED');
    const hasRevoked = this.changeTrails.some((trail) => trail.changeType === 'REVOKED');

    if (!this.changeTrails.length) {
      return 'none';
    }

    if (hasGranted && hasRevoked) {
      return 'mixed';
    }

    if (hasRevoked) {
      const currentLearners = this.learnerList() || [];
      return currentLearners.length === 0 ? 'none' : 'removed';
    }

    return 'granted';
  }

  // We need to keep this map to safely use the learner avatar component (without changing it or other components) which accepts IStudent.
  private mapAccessChangeStudentToIStudent(student: IAccessChangeStudent): IStudent {
    return {
      id: student.id,
      preferredName: `${student.person.firstName} ${student.person.lastName}`,
      studentNumber: '',
      firstname: student.person.firstName,
      lastname: student.person.lastName,
      email: null,
      pictureId: student.imageId,
      pictureUrl: student.imageUrl,
      colour: student.color,
      birthday: new Date(),
      idPassport: '',
      grade: '',
      enrolmentStatus: 'Active' as const,
      enrolmentDocuments: [],
      accountCode: student.accountCode,
      companyCode: '',
      schoolId: '',
      registerClass: '',
      classIds: [],
      extracurricularIds: [],
      attendance: [],
      reportCardUrls: [],
      medicalInfo: { provider: '', mainMemberNumber: '', mainMemberName: '', mainMemberId: '' },
      emergencyContacts: [],
    };
  }

  private getAdminGuardianFromChangeTrails(): IGuardian | IAccessAlertAdmin | undefined {
    const hasLearners = this.learnerList().length > 0;

    if (hasLearners) {
      // Standard path: admin is a fully-loaded guardian in the store
      const changedBy = this.changeTrails.find(
        (trail) => trail.changedBy !== null && String(trail.changedBy).trim() !== '',
      )?.changedBy;

      if (changedBy === null || changedBy === undefined) return undefined;

      const changedByGuardianId = String(changedBy).trim();
      if (!changedByGuardianId) return undefined;

      return this.guardians().find((guardian) => String(guardian.id) === changedByGuardianId);
    }

    // No-learner path: admin is only known via guardianLinks (partial info)
    const links = this.guardianLinks();
    if (!links.length) return undefined;

    let chosenLink = links[0];

    if (this.changeTrails.length > 0) {
      const changedBy = this.changeTrails.find(
        (trail) => trail.changedBy !== null && String(trail.changedBy).trim() !== '',
      )?.changedBy;

      if (changedBy !== null && changedBy !== undefined) {
        const changedByGuardianId = String(changedBy).trim();
        const matchedLink = links.find((link) => link.guardianId === changedByGuardianId);
        if (matchedLink) {
          chosenLink = matchedLink;
        }
      }
    }

    return this.mapLinkedAccountInfoToAccessAlertAdmin(chosenLink);
  }

  private mapLinkedAccountInfoToAccessAlertAdmin(link: ILinkedAccountInfo): IAccessAlertAdmin {
    return {
      firstname: link.guardianName ?? '',
      lastname: link.gaurdianSurname ?? '',
      email: link.guardianEmail?.address ?? '',
    };
  }

  private resolveAdminGuardian(): IGuardian | IAccessAlertAdmin | null {
    return this.getAdminGuardianFromChangeTrails() ?? this.fallbackAdminGuardian();
  }

  getResolvedAdminGuardian(): IGuardian | IAccessAlertAdmin | null {
    return this.resolveAdminGuardian();
  }

  private openAccessAlert(options?: { firstLoginNoLearnersAlert?: boolean }): void {
    const admin = this.resolveAdminGuardian();
    if (!admin) {
      console.warn('No admin guardian available to contact');
      return;
    }

    this.store.dispatch(
      AccessActions.setAccessAlertAdmin({
        accessAlertAdmin: {
          firstname: admin.firstname,
          lastname: admin.lastname,
          email: admin.email,
        },
      }),
    );

    const accessStudents = this.accessChangeStudents();
    const children = this.changeTrails
      .map((trail) => {
        const student = accessStudents.find((accessStudent) => accessStudent.id === trail.studentId);
        if (!student) {
          return null;
        }

        const mappedStudent = this.mapAccessChangeStudentToIStudent(student);
        return { learner: mappedStudent, accessRevoked: trail.changeType === 'REVOKED' };
      })
      .filter((child): child is IAccessAlertChild => child !== null);

    const dialogRef = this.dialog.open(AccessAlertComponent, {
      data: {
        action: this.getAccessAlertAction(),
        children,
        isFirstLoginNoLearnersAlert: options?.firstLoginNoLearnersAlert ?? false,
      },
      width: '558px',
      height: 'auto',
      maxWidth: '90dvw',
    });

    this.alertOpen = true;
    dialogRef.afterClosed().subscribe(() => {
      this.alertOpen = false;
    });
  }
}
